import { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useTokens } from '../contexts/TokenContext';
import { useAutoSave } from '../hooks/useAutoSave';
import { SaveIndicator } from '../components/SaveIndicator';
import TopUpModal from '../components/TopUpModal';
import { ArrowLeft, Copy, Download, Loader2, Check } from 'lucide-react';
import api from '../lib/api';

const EMPTY_FIELDS = {
  title: '', author: '', contact_details: '', synopsis: '',
  main_character: '', dramatic_need: '', central_conflict: '',
  inciting_incident: '', plot_point_1: '', obstacles: '',
  lowest_point: '', final_confrontation: '', resolution: '',
  world: '', tone_style: '', feedback: '',
};

const FIELD_CONFIG = [
  { key: 'title', label: 'Your Film', type: 'input', placeholder: 'What\'s it called?' },
  { key: 'author', label: 'Written By', type: 'input', placeholder: 'Your name' },
  { key: 'contact_details', label: 'Contact', type: 'textarea-sm', placeholder: 'Email, phone, representation — however people can reach you.' },
  { key: 'synopsis', label: 'Your Story', type: 'textarea-lg', placeholder: 'What\'s the film about? Tell the whole story — beginning, middle, and end. Be specific about what happens, who\'s involved, and what\'s at stake. This is the heart of your screenplay.', emphasis: true },
  { key: 'main_character', label: 'Who\'s at the Centre?', type: 'textarea', placeholder: 'Who is your protagonist? What are they like? Where do we find them when the story begins?' },
  { key: 'dramatic_need', label: 'What Do They Want?', type: 'textarea', placeholder: 'What drives your character through this story? What do they need more than anything?' },
  { key: 'central_conflict', label: 'What\'s in the Way?', type: 'textarea', placeholder: 'What\'s the core opposition? What force — internal, external, or both — prevents them from getting what they want?' },
  { key: 'inciting_incident', label: 'What Sparks It?', type: 'textarea', placeholder: 'What happens to shatter the ordinary world? The event that sets everything in motion.' },
  { key: 'plot_point_1', label: 'No Going Back', type: 'textarea', placeholder: 'The moment your character commits fully. After this, there\'s no turning around.' },
  { key: 'obstacles', label: 'What Goes Wrong?', type: 'textarea', placeholder: 'The escalating challenges, setbacks, betrayals, and complications. What makes it harder and harder?' },
  { key: 'lowest_point', label: 'Rock Bottom', type: 'textarea', placeholder: 'The moment of greatest despair. Everything falls apart. All seems lost.' },
  { key: 'final_confrontation', label: 'The Showdown', type: 'textarea', placeholder: 'The climactic scene. How does your character face the central conflict head-on?' },
  { key: 'resolution', label: 'How Does It End?', type: 'textarea', placeholder: 'How does the story resolve? What has changed? Where do we leave your character?' },
  { key: 'world', label: 'The World', type: 'textarea', placeholder: 'Time, place, atmosphere. What does this world look, sound, and feel like?' },
  { key: 'tone_style', label: 'Tone & Style', type: 'textarea-sm', placeholder: "e.g. 'Jaws meets Die Hard.' Used for tone and feel, not story.", isOptional: true },
  { key: 'feedback', label: 'Notes & Feedback', type: 'textarea-lg', placeholder: 'Paste coverage notes, feedback, or your own revision ideas. Clear, specific notes produce the best results.', isFeedback: true },
];

// Story-driven, narrative-aware progress messages (first generation)
const STORY_MESSAGES = [
  'Introducing your main character...',
  'Building your world...',
  'Something is about to change...',
  'The story is taking shape...',
  'Tension is building...',
  'Lines are being crossed...',
  'Your characters are making choices...',
  'Everything is starting to connect...',
  'The story is deepening...',
  'Momentum is building...',
  'Secrets are surfacing...',
  'The pressure is mounting...',
  'Paths are converging...',
  'Nothing will be the same...',
  'The pieces are falling into place...',
  'Your story is finding its voice...',
];

// Rewrite-specific progress messages (when notes are present)
const REWRITE_MESSAGES = [
  'Rewriting your screenplay...',
  'Applying your notes...',
  'Strengthening character arcs...',
  'Refining dialogue...',
  'Adjusting story structure...',
  'Deepening emotional beats...',
  'Sharpening conflict...',
  'Reworking pacing...',
  'Improving scene transitions...',
  'Elevating subtext...',
  'Tightening the narrative...',
  'Your rewrite is taking shape...',
];

const ACT_MOMENTS = {
  1: { title: 'Act I Complete', sub: 'Your story has begun.' },
  2: { title: 'Act II Complete', sub: 'Everything has changed.' },
  3: { title: 'Act III Complete', sub: 'The ending has arrived.' },
};

export default function ProjectPage() {
  const { projectId } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user } = useAuth();
  const { balance, refreshBalance, isLowBalance, hasFreeScreenplay } = useTokens();
  const [fields, setFields] = useState(EMPTY_FIELDS);
  const [loaded, setLoaded] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [genError, setGenError] = useState('');
  const [screenplay, setScreenplay] = useState('');
  const [screenplayMeta, setScreenplayMeta] = useState({});
  const [copied, setCopied] = useState(false);
  const [genProgress, setGenProgress] = useState({ completed: 0, total: 18, backendMsg: '' });
  const [progressMsg, setProgressMsg] = useState('');
  const [livePartialText, setLivePartialText] = useState('');
  const [liveStats, setLiveStats] = useState({ parts: 0, scenes: 0, words: 0, pages: 0 });
  const [actMoment, setActMoment] = useState(null);
  const [topUpOpen, setTopUpOpen] = useState(false);
  const [topUpMessage, setTopUpMessage] = useState('');
  const [pendingAction, setPendingAction] = useState(null);
  const lastActRef = useRef(0);
  const isRewriteRef = useRef(false);
  const progressIdx = useRef(0);
  const outputRef = useRef(null);
  const lastProgressChangeRef = useRef(Date.now());
  const lastSeenPartsRef = useRef(-1);

  const saveFn = useCallback(async (data) => {
    await api.put(`/projects/${projectId}/fields`, data);
  }, [projectId]);

  const { saveStatus, flush } = useAutoSave(
    `sp_fields_${projectId}`, fields, saveFn, { enabled: loaded }
  );

  useEffect(() => {
    if (!projectId) return;
    (async () => {
      try {
        const { data } = await api.get(`/projects/${projectId}/fields`);
        if (data && Object.keys(data).length > 0) setFields(prev => ({ ...prev, ...data }));
        try { const { data: sp } = await api.get(`/projects/${projectId}/screenplay-text`); if (sp?.text) { setScreenplay(sp.text); setScreenplayMeta({ wordCount: sp.word_count, pages: sp.estimated_pages }); } } catch (e) {}
        try {
          const { data: status } = await api.get(`/projects/${projectId}/generation/status`);
          if (status?.status === 'running') {
            setGenerating(true);
            setGenProgress({ completed: status.scenes_completed || 0, total: status.total_scenes || 18, backendMsg: status.message || '' });
          }
        } catch (e) {}
      } catch (e) { console.error(e); }
      finally { setLoaded(true); }
    })();
  }, [projectId]);

  // Story-driven message rotation (uses rewrite messages when notes are present)
  useEffect(() => {
    if (!generating) { setProgressMsg(''); progressIdx.current = 0; return; }
    const messages = isRewriteRef.current ? REWRITE_MESSAGES : STORY_MESSAGES;
    setProgressMsg(messages[0]);
    const interval = setInterval(() => {
      progressIdx.current = (progressIdx.current + 1) % messages.length;
      setProgressMsg(messages[progressIdx.current]);
    }, 5000);
    return () => clearInterval(interval);
  }, [generating]);

  // Detect act completion moments
  useEffect(() => {
    if (!generating) { lastActRef.current = 0; return; }
    const completed = genProgress.completed;
    // Act I done at part 6, Act II at 12, Act III at 18
    let currentAct = 0;
    if (completed >= 18) currentAct = 3;
    else if (completed >= 12) currentAct = 2;
    else if (completed >= 6) currentAct = 1;

    if (currentAct > lastActRef.current && currentAct < 3) {
      // Show act completion moment
      lastActRef.current = currentAct;
      setActMoment(ACT_MOMENTS[currentAct]);
      setTimeout(() => setActMoment(null), 2500);
    }
  }, [genProgress.completed, generating]);

  // Poll generation status
  useEffect(() => {
    if (!generating || !projectId) return;
    let cancelled = false;
    lastProgressChangeRef.current = Date.now();
    lastSeenPartsRef.current = -1;

    const poll = async () => {
      if (cancelled) return;
      try {
        const { data } = await api.get(`/projects/${projectId}/generation/status`);
        if (cancelled) return;
        if (!data || data.status === 'dismissed' || data.status === 'idle') { setGenerating(false); return; }

        // Track progress changes for client-side stall detection
        const currentParts = data.scenes_completed ?? 0;
        if (currentParts !== lastSeenPartsRef.current) {
          lastSeenPartsRef.current = currentParts;
          lastProgressChangeRef.current = Date.now();
        }

        if (data.scenes_completed !== undefined) {
          setGenProgress({ completed: data.scenes_completed, total: data.total_scenes || 18, backendMsg: data.message || '' });
        }
        if (data.status === 'complete') {
          setGenerating(false);
          setActMoment(ACT_MOMENTS[3]);
          setTimeout(() => setActMoment(null), 2500);
          try { const { data: sp } = await api.get(`/projects/${projectId}/screenplay-text`); if (sp?.text) { setScreenplay(sp.text); setScreenplayMeta({ wordCount: sp.word_count, pages: sp.estimated_pages }); } } catch (e) {}
          refreshBalance();
          setTimeout(() => outputRef.current?.scrollIntoView({ behavior: 'smooth' }), 600);
          return;
        }
        if (data.status === 'failed') {
          setGenerating(false);
          const msg = data.message === 'Generation timed out'
            ? 'Writing paused — the generation stalled. Your partial text is saved. Please try again.'
            : (data.message || 'Generation could not be completed. Please try again.');
          setGenError(msg);
          refreshBalance();
          return;
        }
        if (data.status === 'cancelled') { setGenerating(false); refreshBalance(); return; }

        // Client-side stall detection: if no progress change for 7 minutes, treat as stalled
        const minutesSinceChange = (Date.now() - lastProgressChangeRef.current) / 60000;
        if (minutesSinceChange > 7) {
          setGenerating(false);
          setGenError('Writing paused — the generation stalled. Your partial text is saved. Please try again.');
          refreshBalance();
          return;
        }

        setTimeout(poll, 4000);
      } catch (e) { if (!cancelled) setTimeout(poll, 6000); }
    };
    setTimeout(poll, 2000);
    return () => { cancelled = true; };
  }, [generating, projectId, refreshBalance]);

  // Poll partial screenplay text during generation
  useEffect(() => {
    if (!generating || !projectId) return;
    let cancelled = false;
    const pollPartial = async () => {
      if (cancelled) return;
      try {
        const { data } = await api.get(`/projects/${projectId}/screenplay-partial`);
        if (cancelled) return;
        if (data?.text) {
          setLivePartialText(data.text);
          if (data.stats) {
            setLiveStats({
              parts: data.stats.parts_done || 0,
              scenes: data.stats.scenes || 0,
              words: data.stats.words || 0,
              pages: data.stats.pages || 0,
            });
          }
          setTimeout(() => {
            const el = document.querySelector('[data-testid="screenplay-output"]');
            if (el) el.scrollTop = el.scrollHeight;
          }, 100);
        }
      } catch (e) {}
      if (!cancelled) setTimeout(pollPartial, 5000);
    };
    setTimeout(pollPartial, 4000);
    return () => { cancelled = true; };
  }, [generating, projectId]);

  // Clear partial on completion
  useEffect(() => {
    if (!generating && screenplay) {
      setLivePartialText('');
      setLiveStats({ parts: 0, scenes: 0, words: 0, pages: 0 });
    }
  }, [generating, screenplay]);

  const updateField = (key, value) => setFields(prev => ({ ...prev, [key]: value }));

  const hasFeedback = fields.feedback?.trim().length > 0;
  const hasScreenplay = screenplay?.trim().length > 0;
  const isRewrite = hasScreenplay && hasFeedback;
  const buttonLabel = isRewrite
    ? 'Rewrite with Notes'
    : hasFreeScreenplay ? 'Write your screenplay' : (hasFeedback ? 'Write screenplay with notes' : 'Keep writing your screenplay');

  // Credit check wrapper: intercepts 402 and opens TopUpModal
  const startGeneration = async () => {
    isRewriteRef.current = isRewrite;
    setGenerating(true); setGenError(''); setGenProgress({ completed: 0, total: 18, backendMsg: '' });
    try {
      await flush();
      const { data } = await api.post(`/projects/${projectId}/generate-simple`);
      if (data?.status === 'already_running') {
        // A generation is already in progress — just continue polling
        return;
      }
      if (!data?.job_id && data?.status !== 'started') {
        setGenError(data?.detail || 'Could not start writing. Please try again.');
        setGenerating(false);
      }
    } catch (error) {
      if (error.response?.status === 402) {
        setGenerating(false);
        setTopUpMessage("You'll need a few more credits to keep writing your screenplay.");
        setPendingAction({ type: isRewrite ? 'rewrite' : 'generate', projectId });
        setTopUpOpen(true);
        return;
      }
      const detail = error.response?.data?.detail;
      const msg = typeof detail === 'string' ? detail : 'Could not start writing. Please try again.';
      setGenError(msg);
      setGenerating(false);
    }
  };

  const handleGenerate = startGeneration;

  // Auto-resume after returning from Stripe checkout
  useEffect(() => {
    const resumeType = searchParams.get('resume');
    if (resumeType && loaded && !generating) {
      // Clear the URL param
      window.history.replaceState({}, '', window.location.pathname);
      // Small delay to let balance refresh
      setTimeout(() => startGeneration(), 500);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loaded, searchParams]);

  const handleCopy = async () => {
    try { await navigator.clipboard.writeText(screenplay); } catch (e) { const ta = document.createElement('textarea'); ta.value = screenplay; document.body.appendChild(ta); ta.select(); document.execCommand('copy'); document.body.removeChild(ta); }
    setCopied(true); setTimeout(() => setCopied(false), 2000);
  };

  const handleDownloadPDF = async () => {
    try {
      const response = await api.get(`/projects/${projectId}/export/simple-pdf`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const a = document.createElement('a'); a.href = url; a.download = `${fields.title || 'Screenplay'}.pdf`; document.body.appendChild(a); a.click(); a.remove(); window.URL.revokeObjectURL(url);
    } catch (e) { console.error(e); }
  };

  // Derive current act label from backend progress
  const currentActLabel = genProgress.completed >= 12 ? 'Act III' : genProgress.completed >= 6 ? 'Act II' : 'Act I';

  if (!loaded) return (
    <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: '#0B0F1A' }}>
      <Loader2 className="h-5 w-5 animate-spin text-[#D6A85C]/50" />
    </div>
  );

  return (
    <div className="min-h-screen text-[#F5EDE4] relative" style={{ fontFamily: "'Manrope', sans-serif", background: 'linear-gradient(180deg, #0B0F1A 0%, #0E1320 100%)' }}>
      {/* CSS animations */}
      <style>{`
        @keyframes momentIn { from { opacity: 0; transform: scale(0.96); } to { opacity: 1; transform: scale(1); } }
        @keyframes momentOut { from { opacity: 1; } to { opacity: 0; } }
        .act-moment { animation: momentIn 0.4s ease-out forwards; }
        .act-moment-exit { animation: momentOut 0.6s ease-in 1.8s forwards; }
        @keyframes msgFade { 0% { opacity: 0; transform: translateY(4px); } 15% { opacity: 1; transform: translateY(0); } 85% { opacity: 1; transform: translateY(0); } 100% { opacity: 0; transform: translateY(-4px); } }
        .story-msg { animation: msgFade 5s ease-in-out; }
      `}</style>

      {/* Film grain */}
      <div aria-hidden="true" className="fixed inset-0 z-50 pointer-events-none opacity-[0.03] mix-blend-overlay" style={{ backgroundImage: `url("data:image/svg+xml,%3Csvg viewBox='0 0 256 256' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)'/%3E%3C/svg%3E")`, backgroundRepeat: 'repeat', backgroundSize: '150px' }} />

      {/* Act completion moment overlay */}
      {actMoment && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center pointer-events-none" style={{ backgroundColor: 'rgba(11,15,26,0.85)' }}>
          <div className="act-moment act-moment-exit text-center">
            <h2 className="text-3xl sm:text-4xl font-medium text-[#F5EDE4] mb-3" style={{ fontFamily: "'Playfair Display', serif" }}>
              {actMoment.title}
            </h2>
            <p className="text-sm text-[#D6A85C]/80 italic" style={{ fontFamily: "'Playfair Display', serif" }}>
              {actMoment.sub}
            </p>
          </div>
        </div>
      )}

      <header className="sticky top-0 z-40 backdrop-blur-xl border-b border-[#F5EDE4]/[0.06]" style={{ backgroundColor: 'rgba(11,15,26,0.7)' }}>
        <div className="max-w-3xl mx-auto px-6 h-16 flex items-center justify-between">
          <button onClick={() => navigate('/dashboard')} className="flex items-center gap-2 text-[#F5EDE4]/50 hover:text-[#D6A85C] transition-colors duration-300 text-sm" data-testid="back-to-dashboard">
            <ArrowLeft className="h-4 w-4" />
            <span className="hidden sm:inline">Your Films</span>
          </button>
          <div className="flex items-center gap-5">
            <SaveIndicator status={saveStatus} />
            <button onClick={() => navigate('/pricing')} className="text-xs text-[#F5EDE4]/35 hover:text-[#D6A85C] transition-colors duration-300" data-testid="nav-pricing">Pricing</button>
            {balance !== null && (
              <div className="flex items-center gap-3">
                <span className="text-xs text-[#F5EDE4]/35" style={{ fontFamily: "'JetBrains Mono', monospace" }} data-testid="token-balance">{balance} credits</span>
                {isLowBalance && (
                  <button onClick={() => { setTopUpMessage("You're nearing the end of your available credits."); setTopUpOpen(true); }}
                    className="text-[10px] font-medium text-[#D6A85C]/70 hover:text-[#D6A85C] transition-colors" data-testid="topup-btn">
                    Top up
                  </button>
                )}
              </div>
            )}
          </div>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-6 py-16 pb-32 relative z-10">
        <div className="space-y-10">
          {FIELD_CONFIG.map(({ key, label, type, placeholder, emphasis, isFeedback, isOptional }) => (
            <div key={key} className={isFeedback ? 'mt-20 pt-16 border-t border-[#F5EDE4]/[0.06]' : ''}>
              <label htmlFor={key}
                className={`block mb-3 ${emphasis ? 'text-sm font-medium text-[#F5EDE4] tracking-wide' : 'text-xs font-medium text-[#F5EDE4]/50 uppercase tracking-[0.15em]'}`}
                style={!emphasis ? { fontFamily: "'JetBrains Mono', monospace" } : {}}
              >
                {label}
                {emphasis && <span className="ml-2 text-xs font-normal text-[#D6A85C]/60 normal-case tracking-normal">— the heart of your film</span>}
                {(isFeedback || isOptional) && <span className="ml-2 text-xs font-normal text-[#D6A85C]/60 normal-case tracking-normal">— optional</span>}
              </label>
              {type === 'input' ? (
                <input id={key} data-testid={`field-${key}`} type="text" value={fields[key] || ''} onChange={e => updateField(key, e.target.value)} placeholder={placeholder}
                  className="w-full bg-transparent border-b border-[#F5EDE4]/[0.08] focus:border-[#D6A85C]/40 outline-none py-3 text-[#F5EDE4] placeholder:text-[#F5EDE4]/20 transition-colors duration-300 text-base"
                />
              ) : (
                <textarea id={key} data-testid={`field-${key}`} value={fields[key] || ''} onChange={e => updateField(key, e.target.value)} placeholder={placeholder}
                  rows={type === 'textarea-lg' ? 6 : type === 'textarea-sm' ? 3 : 4}
                  className={`w-full border border-[#F5EDE4]/[0.06] focus:border-[#D6A85C]/30 outline-none p-4 text-[#F5EDE4] placeholder:text-[#F5EDE4]/20 transition-colors duration-300 resize-y text-sm leading-relaxed ${emphasis ? 'border-[#D6A85C]/10' : ''}`}
                  style={{ backgroundColor: 'rgba(245,237,228,0.02)' }}
                />
              )}
              {isFeedback && (
                <div className="mt-3 space-y-1.5" data-testid="feedback-helper">
                  {fields.feedback?.trim() ? (
                    <p className="text-xs text-[#D6A85C]/50 italic" style={{ fontFamily: "'Playfair Display', serif" }}>
                      Your next draft will reflect the notes you've provided.
                    </p>
                  ) : null}
                  <p className="text-xs text-[#F5EDE4]/20">
                    Rewrites interpret your notes and may vary in outcome. Clear, specific notes produce the best results.
                  </p>
                </div>
              )}
            </div>
          ))}
        </div>

        {/* Generate Button */}
        <div className="mt-20 flex flex-col items-center">
          {genError && <p className="text-red-400/80 text-sm mb-4 text-center" data-testid="gen-error">{genError}</p>}
          {generating ? (
            <div className="flex flex-col items-center gap-6 w-full max-w-md" data-testid="gen-progress">
              {/* Current act */}
              <h3 className="text-xl font-medium text-[#F5EDE4]" style={{ fontFamily: "'Playfair Display', serif" }}>
                {isRewriteRef.current ? 'Rewriting' : 'Writing'} {currentActLabel}
              </h3>

              {/* Story-driven message */}
              <p key={progressMsg} className="story-msg text-sm text-[#F5EDE4]/50 italic h-5" style={{ fontFamily: "'Playfair Display', serif" }}>
                {progressMsg}
              </p>

              {/* Live stats */}
              <div className="flex items-center gap-6 text-xs text-[#F5EDE4]/35" style={{ fontFamily: "'JetBrains Mono', monospace" }}>
                {liveStats.parts > 0 && <span>{liveStats.parts}/{genProgress.total} parts</span>}
                {liveStats.scenes > 0 && <span>{liveStats.scenes} scenes</span>}
                {liveStats.words > 0 && <span>{liveStats.words.toLocaleString()} words</span>}
                {liveStats.pages > 0 && <span>~{liveStats.pages} pg</span>}
              </div>

              {/* Progress bar */}
              <div className="w-full">
                <div className="w-full h-[3px] rounded-full overflow-hidden" style={{ backgroundColor: 'rgba(214,168,92,0.08)' }}>
                  <div className="h-full rounded-full transition-all duration-1000 ease-out" style={{ width: `${Math.max(1, (genProgress.completed / genProgress.total) * 100)}%`, backgroundColor: '#D6A85C' }} />
                </div>
                <div className="flex justify-end mt-2">
                  <span className="text-xs text-[#F5EDE4]/20" style={{ fontFamily: "'JetBrains Mono', monospace" }}>
                    ~{Math.max(1, Math.round((genProgress.total - genProgress.completed) * 3.5))} min remaining
                  </span>
                </div>
              </div>
            </div>
          ) : (
            <div className="flex flex-col items-center gap-3">
              {/* Free screenplay banner for entitled members */}
              {hasFreeScreenplay && !hasScreenplay && (
                <p className="text-xs text-[#D6A85C]/60 italic mb-2" style={{ fontFamily: "'Playfair Display', serif" }} data-testid="free-screenplay-banner">
                  Your first screenplay is on us
                </p>
              )}
              <button onClick={handleGenerate} disabled={!fields.synopsis?.trim() || generating}
                className="px-10 py-4 text-sm font-medium disabled:opacity-15 disabled:cursor-not-allowed active:scale-[0.98] transition-all duration-300"
                style={{ backgroundColor: '#D6A85C', color: '#0B0F1A' }}
                data-testid="generate-btn"
              >
                {buttonLabel}
              </button>
              {isRewrite && (
                <p className="text-xs text-[#F5EDE4]/40 text-center max-w-sm" style={{ fontFamily: "'Playfair Display', serif" }} data-testid="rewrite-helper">
                  Your notes will be applied to improve structure, dialogue, and character.
                </p>
              )}
            </div>
          )}
        </div>

        {/* Screenplay Output — progressive during generation, final after */}
        {(screenplay || livePartialText) && (
          <div ref={outputRef} className="mt-24 pt-16 border-t border-[#F5EDE4]/[0.06]">
            <div className="flex items-center justify-between mb-6">
              <div>
                <h2 className="text-xl font-medium text-[#F5EDE4] tracking-tight" style={{ fontFamily: "'Playfair Display', serif" }}>
                  {fields.title || 'Your Screenplay'}
                </h2>
                {!generating && screenplayMeta.pages ? (
                  <p className="text-xs text-[#F5EDE4]/30 mt-2" style={{ fontFamily: "'JetBrains Mono', monospace" }}>
                    ~{screenplayMeta.pages} pages &middot; {screenplayMeta.wordCount?.toLocaleString()} words
                  </p>
                ) : generating && liveStats.words > 0 ? (
                  <>
                    <p className="text-xs text-[#D6A85C]/50 mt-2" style={{ fontFamily: "'JetBrains Mono', monospace" }}>
                      {liveStats.words.toLocaleString()} words &middot; ~{liveStats.pages} pages so far
                    </p>
                    <p className="text-xs text-[#F5EDE4]/20 mt-1 italic" style={{ fontFamily: "'Playfair Display', serif" }}>
                      {isRewriteRef.current ? 'Your screenplay is being rewritten with your notes' : 'Your film is being written in real time'}
                    </p>
                  </>
                ) : null}
              </div>
              {!generating && screenplay && (
                <div className="flex items-center gap-2">
                  <button onClick={handleCopy} className="flex items-center gap-2 px-4 py-2 text-xs text-[#F5EDE4]/50 hover:text-[#D6A85C] border border-[#F5EDE4]/[0.06] hover:border-[#D6A85C]/30 transition-all duration-300" data-testid="copy-btn">
                    {copied ? <Check className="h-3 w-3" /> : <Copy className="h-3 w-3" />}
                    {copied ? 'Copied' : 'Copy'}
                  </button>
                  <button onClick={handleDownloadPDF} className="flex items-center gap-2 px-4 py-2 text-xs text-[#F5EDE4]/50 hover:text-[#D6A85C] border border-[#F5EDE4]/[0.06] hover:border-[#D6A85C]/30 transition-all duration-300" data-testid="download-pdf-btn">
                    <Download className="h-3 w-3" /> PDF
                  </button>
                </div>
              )}
            </div>
            <div
              className="border border-[#F5EDE4]/[0.06] p-8 text-sm leading-relaxed text-[#F5EDE4]/70 whitespace-pre-wrap max-h-[80vh] overflow-y-auto"
              style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: '13px', backgroundColor: 'rgba(245,237,228,0.02)' }}
              data-testid="screenplay-output"
            >
              {generating ? livePartialText : screenplay}
            </div>
          </div>
        )}
      </main>

      {/* Top-up Modal */}
      <TopUpModal
        open={topUpOpen}
        onClose={() => setTopUpOpen(false)}
        message={topUpMessage}
        pendingAction={pendingAction}
      />
    </div>
  );
}
